import { call, put, select, takeEvery, takeLatest, takeLeading } from "redux-saga/effects";

import Email from "@mapmycustomers/shared/types/email/Email";
import EmailThread from "@mapmycustomers/shared/types/email/EmailThread";
import EntityEmailSync from "@mapmycustomers/shared/types/email/EntityEmailSync";
import Organization from "@mapmycustomers/shared/types/Organization";
import ListResponse from "@mapmycustomers/shared/types/viewModel/ListResponse";
import PlatformListRequest from "@mapmycustomers/shared/types/viewModel/platformModel/PlatformListRequest";

import { callApi } from "@app/store/api/callApi";
import { handleError } from "@app/store/errors/actions";
import { getOrganizationId } from "@app/store/iam";

import { EntityEmailsState } from ".";
import {
  closeParentEmailThread,
  dismissEntityEmailsSync,
  fetchEmailAttachment,
  fetchEntityEmailSync,
  loadChildEmails,
  loadParentEmailThreads,
  loadSendEmail,
  openParentEmailThread,
  searchParentEmailThreads,
} from "./actions";
import {
  getEmailThreadsEntity,
  getEntityEmailSync,
  getParentEmailThread,
  getParentEmailThreads,
  getParentEmailThreadsFolder,
  getParentEmailThreadsNeedRefresh,
  getParentEmailThreadsSearchText,
} from "./selectors";

export function* fetchParentEmailThreads({ $offset }: Pick<PlatformListRequest, "$offset"> = {}) {
  const orgId: Organization["id"] = yield select(getOrganizationId);

  const entity: Required<EntityEmailsState>["entity"] = yield select(getEmailThreadsEntity);
  const parentEmailThreadsFolder: EntityEmailsState["parentEmailThreadsFolder"] = yield select(
    getParentEmailThreadsFolder
  );
  const parentEmailThreadsSearchText: EntityEmailsState["parentEmailThreadsSearchText"] =
    yield select(getParentEmailThreadsSearchText);

  const response: ListResponse<EmailThread> = yield callApi(
    "fetchEntityEmailThreads",
    orgId,
    entity.entity,
    entity.id,
    {
      $filters: {
        $and: [
          {
            parent: true,
          },
          {
            threadId: {
              $ne: null,
            },
          },
        ],
      },
      $folder: parentEmailThreadsFolder,
      $limit: 10,
      $offset,
      $order: "-date",
      $search: parentEmailThreadsSearchText,
    }
  );

  return response;
}

export function* onFetchEntityEmailSync() {
  try {
    const orgId: Organization["id"] = yield select(getOrganizationId);
    const entity: Required<EntityEmailsState>["entity"] = yield select(getEmailThreadsEntity);

    const entityEmailSync: EntityEmailSync = yield callApi(
      "fetchEntityEmailSync",
      orgId,
      entity.entity,
      entity.id
    );

    yield put(fetchEntityEmailSync.success(entityEmailSync));
  } catch (error) {
    yield put(fetchEntityEmailSync.failure());
    yield put(handleError({ error }));
  }
}

export function* onDismissEntityEmailsSync() {
  try {
    const orgId: Organization["id"] = yield select(getOrganizationId);
    const entityEmailSync: Required<EntityEmailsState>["entityEmailSync"] = yield select(
      getEntityEmailSync
    );

    yield callApi("dismissEntityEmailSync", orgId, entityEmailSync.id);

    yield put(dismissEntityEmailsSync.success());
  } catch (error) {
    yield put(dismissEntityEmailsSync.failure());
    yield put(handleError({ error }));
  }
}

export function* onSearchParentEmailThreads() {
  try {
    const emailThreadsResponse: ListResponse<EmailThread> = yield fetchParentEmailThreads();

    yield put(
      searchParentEmailThreads.success({
        data: emailThreadsResponse.data,
        totalCount: emailThreadsResponse.total,
      })
    );
  } catch (error) {
    yield put(searchParentEmailThreads.failure());
    yield put(handleError({ error }));
  }
}

export function* onLoadParentEmailThreads() {
  try {
    const parentEmailThreads: EntityEmailsState["parentEmailThreads"] = yield select(
      getParentEmailThreads
    );

    const emailThreadsResponse: ListResponse<EmailThread> = yield fetchParentEmailThreads({
      $offset: parentEmailThreads.length,
    });

    yield put(
      loadParentEmailThreads.success({
        data: emailThreadsResponse.data,
        totalCount: emailThreadsResponse.total,
      })
    );
  } catch (error) {
    yield put(loadParentEmailThreads.failure());
    yield put(handleError({ error }));
  }
}

export function* onOpenParentEmailThread() {
  yield put(loadChildEmails.request());
}

export function* onCloseParentEmailThread() {
  const parentEmailThreadsNeedRefresh: boolean = yield select(getParentEmailThreadsNeedRefresh);
  if (parentEmailThreadsNeedRefresh) {
    yield put(searchParentEmailThreads.request());
  }
}

export function* onLoadChildEmails() {
  try {
    const orgId: Organization["id"] = yield select(getOrganizationId);
    const parentEmailThread: EmailThread = yield select(getParentEmailThread);

    const childEmails: Email[] = [];

    while (true) {
      const response: ListResponse<Email> = yield callApi("fetchEmails", orgId, {
        $filters: {
          $and: [
            {
              threadId: {
                $eq: parentEmailThread.threadId,
              },
            },
          ],
        },
        $limit: 1000,
        $offset: childEmails.length,
        $order: "date",
      });

      childEmails.push(
        ...response.data.filter(
          (email) => !childEmails.some((loadedEmailThread) => email.id === loadedEmailThread.id)
        )
      );

      if (!response.total || response.total <= childEmails.length) {
        break;
      }
    }
    yield put(loadChildEmails.success(childEmails));
  } catch (error) {
    yield put(loadChildEmails.failure());
    yield put(handleError({ error }));
  }
}

export function* onLoadSendEmail({
  payload: { messageId, onFailure, onSuccess },
}: ReturnType<typeof loadSendEmail.request>) {
  try {
    const orgId: Organization["id"] = yield select(getOrganizationId);

    const response: ListResponse<Email> = yield callApi("fetchEmails", orgId, {
      $filters: {
        $and: [
          {
            messageId: {
              $eq: messageId,
            },
          },
        ],
      },
    });

    const email = response.data[0];
    if (!email) {
      yield call(onFailure);
      yield put(loadSendEmail.failure());
      return;
    }

    yield call(onSuccess);
    yield put(loadSendEmail.success(email));
  } catch (error) {
    yield call(onFailure);
    yield put(loadSendEmail.failure());
    yield put(handleError({ error }));
  }
}

export function* onFetchEmailAttachment({
  payload: { attachmentId, messageId, onFailure, onSuccess },
}: ReturnType<typeof fetchEmailAttachment.request>) {
  try {
    const orgId: Organization["id"] = yield select(getOrganizationId);
    const file: Blob = yield callApi("fetchEmailAttachment", orgId, attachmentId, messageId, {
      responseType: "blob",
    });

    yield call(onSuccess, file);
  } catch (error) {
    yield call(onFailure);
    yield put(handleError({ error }));
  }
}

export function* entityEmailsSaga() {
  yield takeLatest(fetchEntityEmailSync.request, onFetchEntityEmailSync);
  yield takeLeading(dismissEntityEmailsSync.request, onDismissEntityEmailsSync);
  yield takeLatest(searchParentEmailThreads.request, onSearchParentEmailThreads);
  yield takeLatest(loadParentEmailThreads.request, onLoadParentEmailThreads);
  yield takeEvery(openParentEmailThread, onOpenParentEmailThread);
  yield takeEvery(closeParentEmailThread, onCloseParentEmailThread);
  yield takeLatest(loadChildEmails.request, onLoadChildEmails);
  yield takeLatest(loadSendEmail.request, onLoadSendEmail);
  yield takeEvery(fetchEmailAttachment.request, onFetchEmailAttachment);
}
